import React, { useState, useMemo } from 'react';
import { useParams, useLocation } from 'wouter';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Shield,
  Clock,
  CreditCard,
  Ban,
  ChevronRight,
  Building2,
  Target,
  Lightbulb,
  HelpCircle,
  CheckCircle2,
  Search,
  X,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Grid3X3
} from 'lucide-react';
import { getCityBySlug } from '../data/cities';
import Logo from '../components/Logo';
import { Button } from '../components/ui/Button';
import { SupportFooter } from '../components/SupportFooter';
import { NotFoundPage } from './NotFoundPage';
import { tools } from '../utils/tools';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { getToolLogoUrl } from '../utils/tools/logos';
import { generateToolSlug } from '../utils/slugs';

export function CityDirectoryPage() {
  const params = useParams<{ citySlug: string }>();
  const [_, navigate] = useLocation();
  const city = getCityBySlug(params.citySlug || '');

  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(24);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const categories = useMemo(() => {
    return [...new Set(tools.map(tool => tool.category))].sort();
  }, []);

  const filteredTools = useMemo(() => {
    return tools.filter(tool => {
      const matchesSearch =
        searchTerm === '' ||
        tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.category.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        activeCategory === null ||
        tool.category === activeCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, activeCategory]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    tools.forEach(tool => {
      counts[tool.category] = (counts[tool.category] || 0) + 1;
    });
    return counts;
  }, []);

  const displayedCategories = showAllCategories ? categories : categories.slice(0, 8);

  if (!city) {
    return <NotFoundPage />;
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": city.faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `btoolme - Software Tools for ${city.name} Businesses`,
    "description": city.metaDescription,
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "addressRegion": city.state,
      "addressCountry": "US"
    },
    "provider": {
      "@type": "Organization",
      "name": "btoolme"
    }
  };

  const handleCTA = () => {
    navigate('/questionnaire');
  };

  const scrollToDirectory = () => {
    const element = document.getElementById('tools-directory');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" id="main-content">
      <Helmet>
        <title>{city.seoTitle}</title>
        <meta name="description" content={city.metaDescription} />
        <link rel="canonical" href={`https://btoolme.com/directory/${city.slug}`} />
        <meta property="og:title" content={city.seoTitle} />
        <meta property="og:description" content={city.metaDescription} />
        <meta property="og:url" content={`https://btoolme.com/directory/${city.slug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={city.seoTitle} />
        <meta name="twitter:description" content={city.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <a href="/" className="text-gray-500 hover:text-gray-700">Home</a>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <li>
              <a href="/directory" className="text-gray-500 hover:text-gray-700">Directory</a>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <li className="text-gray-900 font-medium">{city.name}, {city.state}</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100/40 rounded-full blur-3xl" />
          <div className="absolute top-20 -left-20 w-60 h-60 bg-cyan-100/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <a href="/" className="inline-block mb-6">
              <Logo />
            </a>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-5">
              {city.h1}
            </h1>

            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Get a personalized software blueprint tailored to your business needs.
              Find the right tools without the guesswork.
            </p>

            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                <Button
                  onClick={handleCTA}
                  className="group px-8 py-4 text-lg w-full sm:w-auto"
                  size="lg"
                >
                  Build My Software Blueprint
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                <button
                  onClick={scrollToDirectory}
                  className="group flex items-center gap-2 px-6 py-4 text-gray-700 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all font-medium w-full sm:w-auto justify-center"
                >
                  <Grid3X3 className="w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                  Browse Tools
                </button>
              </div>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
                <Shield className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">
                  100% Confidence Guaranteed
                </span>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span>3-minute questionnaire</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-500" />
                <span>One-time payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-blue-500" />
                <span>No subscriptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Software Decisions Feel Harder */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Why software decisions feel harder in {city.name}
            </h2>
          </div>

          <div className="prose prose-lg text-gray-600 max-w-none">
            <p>{city.intro}</p>
          </div>
        </div>
      </section>

      {/* Common Business Types */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Common business types in {city.name}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {city.localBusinessTypes.map((type, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200"
              >
                <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What btoolme Helps With */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              A smarter way to choose your software
            </h2>
          </div>

          <p className="text-gray-600 mb-6">
            btoolme gives you a personalized blueprint based on how your business actually works—not generic "top 10" lists.
          </p>

          <div className="space-y-3">
            {city.localAngleBullets.map((bullet, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <ArrowRight className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{bullet}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suggested Categories */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Suggested starting categories
          </h2>
          <p className="text-gray-600 mb-8">
            These tool categories are commonly relevant for {city.name} businesses.
            Your blueprint will identify exactly which ones matter for your situation.
          </p>

          <div className="flex flex-wrap gap-3">
            {city.suggestedCategories.map((category, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-gray-700 text-sm font-medium hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Directory */}
      <section id="tools-directory" className="py-12 sm:py-16 bg-white scroll-mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Grid3X3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                The Best 100 Business Tools
              </h2>
              <p className="text-gray-600 mt-1">
                Explore our curated collection of top-rated business software
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Category filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === null
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All (100)
              </button>
              {displayedCategories.map(category => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(activeCategory === category ? null : category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category} ({categoryCounts[category]})
                </button>
              ))}
              {categories.length > 8 && (
                <button
                  onClick={() => setShowAllCategories(!showAllCategories)}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all flex items-center gap-1"
                >
                  {showAllCategories ? (
                    <>Show Less <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>{categories.length - 8} More <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Tools Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTools.slice(0, visibleCount).map((tool, index) => (
              <React.Fragment key={tool.name}>
                <a
                  href={`/tools/${generateToolSlug(tool)}`}
                  className="group bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <ImageWithFallback
                        src={getToolLogoUrl(tool)}
                        alt={`${tool.name} logo`}
                        className="w-10 h-10 object-contain"
                        toolId={tool.id}
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                        {tool.name}
                      </h3>
                      <p className="text-xs text-blue-600 font-medium">{tool.category}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {tool.description}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {tool.pricing?.slice(0, 2).map((price, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                        >
                          {price}
                        </span>
                      ))}
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  </div>
                </a>

                {/* Inline Quiz CTA - appears after 8th and 20th tool */}
                {(index === 7 || index === 19) && (
                  <div className="col-span-1 sm:col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                    <div className="flex-1 text-center sm:text-left">
                      <p className="text-sm text-blue-400 font-medium mb-1">
                        {index === 7 ? 'Need help choosing?' : 'Still browsing?'}
                      </p>
                      <p className="text-white font-semibold">
                        {index === 7
                          ? 'Get personalized recommendations based on your business needs.'
                          : 'Our quiz matches you with the right tools in just 3 minutes.'}
                      </p>
                    </div>
                    <button
                      onClick={handleCTA}
                      className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm"
                    >
                      Take the Quiz
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Load More */}
          {visibleCount < filteredTools.length && (
            <div className="mt-8 text-center">
              <button
                onClick={() => setVisibleCount(prev => prev + 24)}
                className="px-8 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
              >
                Load More ({filteredTools.length - visibleCount} remaining)
              </button>
            </div>
          )}

          {/* No results */}
          {filteredTools.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No tools found matching your search.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setActiveCategory(null);
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-6">
            {city.faq.map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-6 last:border-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.q}
                </h3>
                <p className="text-gray-600">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to find the right tools for your {city.name} business?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Stop researching endlessly. Get a personalized blueprint in 3 minutes.
          </p>

          <div className="flex flex-col items-center gap-4">
            <Button
              onClick={handleCTA}
              className="group px-10 py-5 text-lg bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-xl shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 hover:scale-[1.02]"
              size="lg"
            >
              Build My Software Blueprint
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-white">
                100% Confidence Guaranteed
              </span>
            </div>
          </div>

          <p className="mt-8 text-sm text-gray-400">
            Questions? <a href="mailto:btoolmepro@gmail.com" className="text-gray-300 hover:text-white underline">Contact us</a>
          </p>
        </div>
      </section>

      <SupportFooter variant="dark" className="bg-gray-900" />
    </div>
  );
}
